import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SubHeader from "components/common/SubHeader";
import NoData from "components/common/NoData";
import LoadingSpinner from "components/common/LoadingSpiner";
import { getTravelProductList } from "api/travel";
import useRefreshCallback from "hooks/useRefreshCallback";
import Seo from "components/common/Seo";
import LoginBottomSheet from "components/common/LoginBottomSheet";
import TourCard, { isAlwaysTour, isDepartureConfirmed } from "components/tour/TourCard";
import { TourHeartIcon } from "components/tour/TourLikeButton";
import useTourLikes from "hooks/useTourLikes";

const FILTERS = [
    { key: "ALL", label: "전체" },
    { key: "ALWAYS", label: "상시투어" },
    { key: "CONFIRMED", label: "출발확정" },
];

function TourPage() {
    const navigate = useNavigate();

    const [tourList, setTourList] = useState();
    const [activeFilter, setActiveFilter] = useState("ALL");
    const [page, setPage] = useState(1);
    const [hasNext, setHasNext] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const sentinelRef = useRef(null);
    const [showLoginBottomSheet, setShowLoginBottomSheet] = useState(false);
    const { isLoggedIn, isLiked, toggleLike } = useTourLikes();

    const fetchInitial = useCallback(async () => {
        const res = await getTravelProductList(1);
        if (res) {
            setTourList(res.results || []);
            setHasNext(Boolean(res.next));
            setPage(1);
        } else {
            setTourList([]);
            setHasNext(false);
            setPage(1);
        }
    }, []);

    const loadMore = useCallback(async () => {
        if (!hasNext || isLoadingMore) return;
        setIsLoadingMore(true);
        try {
            const nextPage = page + 1;
            const res = await getTravelProductList(nextPage);
            if (res) {
                setTourList((prev) => [...(prev || []), ...(res.results || [])]);
                setHasNext(Boolean(res.next));
                setPage(nextPage);
            }
        } finally {
            setIsLoadingMore(false);
        }
    }, [hasNext, isLoadingMore, page]);

    useEffect(() => {
        fetchInitial();
    }, [fetchInitial]);

    useRefreshCallback(() => {
        return fetchInitial();
    });

    useEffect(() => {
        const node = sentinelRef.current;
        if (!node || !hasNext) return undefined;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) loadMore();
            },
            { rootMargin: "200px 0px" }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasNext, loadMore]);

    const filteredList = useMemo(() => {
        if (!tourList) return tourList;
        if (activeFilter === "ALWAYS") return tourList.filter(isAlwaysTour);
        if (activeFilter === "CONFIRMED") return tourList.filter(isDepartureConfirmed);
        return tourList;
    }, [tourList, activeFilter]);

    const handleTourCardClick = (id) => {
        navigate(`/tour/detail/${id}`);
    };

    const handleLikeClick = async (id) => {
        const handled = await toggleLike(id);
        if (!handled) setShowLoginBottomSheet(true);
    };

    return (
        <div className="tour-page">
            <Seo
                title="다이빙 투어"
                description="국내외 프리다이빙·스쿠버다이빙 투어 상품을 조회하고 예약하세요. 더딥스, 와이낫다이브 등 전문 다이빙 업체의 투어를 한눈에 비교할 수 있습니다."
            />
            <SubHeader
                title={"투어"}
                menus={[{
                    name: "찜한 투어",
                    ariaLabel: "찜한 투어 보기",
                    content: <TourHeartIcon liked={false} />,
                    callback: () => {
                        if (!isLoggedIn) {
                            setShowLoginBottomSheet(true);
                            return;
                        }
                        navigate("/mybuccl/tour-likes");
                    },
                }]}
            />

            <div className="tour-filter-bar">
                {FILTERS.map((f) => (
                    <button
                        key={f.key}
                        type="button"
                        className={`tour-filter-chip${activeFilter === f.key ? " active" : ""}`}
                        onClick={() => setActiveFilter(f.key)}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {filteredList && filteredList.length === 0 && (
                <div className="no-data-wrap">
                    <NoData title="조건에 맞는 투어가 없습니다." />
                </div>
            )}
            <div className="package-grid">
                {filteredList &&
                    filteredList.map((t) => (
                        <TourCard
                            key={`tour-list-${t.id}`}
                            tour={t}
                            onClick={handleTourCardClick}
                            liked={isLiked(t.id)}
                            onLikeClick={handleLikeClick}
                        />
                    ))}
            </div>
            <div ref={sentinelRef} style={{ height: 1 }} />
            {isLoadingMore && (
                <div className="tour-loading-more">
                    <LoadingSpinner />
                </div>
            )}
            <LoginBottomSheet
                isOpen={showLoginBottomSheet}
                onClose={() => setShowLoginBottomSheet(false)}
            />
        </div>
    );
}

export default TourPage;
